import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { Search, ChevronDown, LogOut, ALargeSmall, History } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/lib/auth'
import { useVisibleNav } from './useVisibleNav'
import { FONT_SCALES, loadFontScale, parseFontScale, saveFontScale } from './font-scale'

/** AWS Console풍 글로벌 탑바 — 다크 네이비, 로고 · 메뉴 검색 · 유저 메뉴 */
export function TopBar() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const [fontScale, setFontScale] = useState(loadFontScale)
  // 메뉴 검색도 권한 필터 적용 (FIKET-417)
  const { flat: navItems } = useVisibleNav()

  const results = query.trim()
    ? navItems.filter((item) => item.label.toLowerCase().includes(query.trim().toLowerCase()))
    : []

  return (
    <header className="sticky top-0 z-50 flex h-10 items-center gap-4 bg-[#232f3e] px-4 text-sm text-[#d1d5db]">
      <Link to="/v2" className="flex items-center gap-2 font-bold text-white hover:text-[#ec7211]">
        Fiket
        <span className="rounded-[2px] bg-[#414d5c] px-1.5 py-0.5 text-[0.6875rem] font-normal text-[#d1d5db]">
          콘솔
        </span>
      </Link>

      {/* 메뉴 검색 — V2_NAV 항목 필터 */}
      <div className="relative mx-auto w-full max-w-md">
        <Search className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-[#8d99a8]" />
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setSearchOpen(true)
          }}
          onFocus={() => setSearchOpen(true)}
          onBlur={() => setSearchOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setSearchOpen(false)
            if (e.key === 'Enter' && results[0]) {
              navigate(results[0].to)
              setQuery('')
              setSearchOpen(false)
            }
          }}
          placeholder="메뉴 검색"
          className="h-7 w-full rounded-[2px] border border-[#414d5c] bg-[#2a3746] pr-2 pl-7 text-[0.8125rem] text-white placeholder:text-[#8d99a8] focus:border-[#0972d3] focus:outline-none"
        />
        {searchOpen && results.length > 0 && (
          <ul className="absolute top-full right-0 left-0 z-50 mt-1 rounded-[2px] border border-[#d5dbdb] bg-white py-1 shadow-md">
            {results.map((item) => (
              <li key={item.to}>
                {/* onMouseDown — input blur보다 먼저 실행되도록 */}
                <button
                  type="button"
                  onMouseDown={() => {
                    navigate(item.to)
                    setQuery('')
                    setSearchOpen(false)
                  }}
                  className="block w-full px-3 py-1.5 text-left text-[0.8125rem] text-[#16191f] hover:bg-[#f1faff] hover:text-[#0972d3]"
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* 글자 크기 — html[data-font-scale] 로 v2 전체 비례 확대, localStorage 저장 (FIKET-384) */}
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label="글자 크기"
          title="글자 크기"
          className="flex items-center gap-1 rounded-[2px] px-2 py-1 hover:bg-[#414d5c] hover:text-white focus:outline-none"
        >
          <ALargeSmall className="size-4" />
          <ChevronDown className="size-3.5" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-40">
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">글자 크기</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuRadioGroup
            value={fontScale}
            onValueChange={(v) => {
              const next = parseFontScale(v)
              saveFontScale(next)
              setFontScale(next)
            }}
          >
            {FONT_SCALES.map((s) => (
              <DropdownMenuRadioItem key={s.key} value={s.key}>
                {s.label}
                <span className="ml-auto text-xs text-muted-foreground">{s.bodyPx}px</span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center gap-1 rounded-[2px] px-2 py-1 hover:bg-[#414d5c] hover:text-white focus:outline-none">
          {user?.name ?? user?.email ?? '관리자'}
          <ChevronDown className="size-3.5" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-48">
          <DropdownMenuLabel className="font-normal">
            <div className="text-xs text-muted-foreground">{user?.email}</div>
            {/* 역할·권한 그룹 표시 (FIKET-417) */}
            <div className="mt-0.5 text-xs text-muted-foreground">
              {user?.role === 'ADMIN' ? '최고 관리자' : user?.adminRole ? `권한 그룹: ${user.adminRole.name}` : user?.role}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {/* v1 레거시 트리 진입 — 로그인 직후 기본 진입이 v2가 된 뒤 v1 전용 기능(템플릿·에셋·시스템)으로 가는 링크(루트 / 직접 진입도 가능). v1은 ADMIN 전용(LegacyAdminOnly)·점진 폐쇄 예정 */}
          {user?.role === 'ADMIN' && (
            <>
              <DropdownMenuItem onClick={() => navigate('/ops/dashboard')}>
                <History className="size-3.5" />
                레거시 어드민(v1)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onClick={() => logout()}>
            <LogOut className="size-3.5" />
            로그아웃
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  )
}
